<template>
  <div class="flex flex-col bg-white" style="border-bottom: 1px solid #e5e7eb;">
    <!-- Top row with back button, logo, and expand/close icon -->
    <div class="flex justify-between items-center p-4">
      <!-- Left side: Back button + End Chat button -->
      <div class="flex items-center space-x-3">

        
        <!-- End Chat button - Conditional styling based on chat state -->
        <button 
          @click="endChat" 
          :disabled="!canEndChat"
          :title="canEndChat ? 'Chat beenden' : 'Chat noch nicht gestartet'"
          class="transition-colors group relative"
          :class="{
            'cursor-pointer text-gray-600 hover:text-red-600': canEndChat,
            'cursor-not-allowed text-gray-300': !canEndChat
          }"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
          
          <!-- Tooltip for disabled state -->
          <div 
            v-if="!canEndChat" 
            class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none"
          >
            Chat noch nicht gestartet
          </div>
        </button>
      </div>
      
      <!-- Logo with Status -->
      <div class="flex items-center space-x-3">
        <img 
          src="../../assets/catLogo.png"
          alt="Logo" 
          class="h-8 w-auto"
        />
        <!-- ✅ FIXED: Use styles.companyID (with capital D) instead of styles.companyId -->
        <StatusDropdown :companyId="styles.companyID || styles.companyId" />
      </div>
      
      <!-- Desktop: Show expand/collapse functionality -->
      <button 
        v-if="!isMobile" 
        @click="expandChat" 
        class="cursor-pointer text-gray-600 hover:text-gray-800 transition-colors"
      >
        <!-- Expand icon (when not expanded) -->
        <svg v-if="!isExpanded" xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5" />
        </svg>
        <!-- Close/X icon (when expanded) -->
        <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
      
      <!-- Mobile: Show close widget button -->
      <button 
        v-if="isMobile" 
        @click="closeWidget" 
        class="cursor-pointer text-gray-600 hover:text-gray-800 transition-colors"
      >
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          class="h-6 w-6" 
          viewBox="0 0 20 20" 
          fill="currentColor"
        >
          <path
            fill-rule="evenodd"
            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
            clip-rule="evenodd"
          />
        </svg>
      </button>
    </div>
  </div>
</template>

<script>
import StatusDropdown from '../StatusDropdown.vue'
import { chatStateManager } from '../../lib/chatStateManager'

export default {
  name: 'NewChatHeader',
  components: {
    StatusDropdown
  },
  props: {
    styles: {
      type: Object,
      default: () => ({
        bubbleColor: '#3b82f6',
        companyName: 'Chatly',
        companyLogo: null
      })
    },
    isExpanded: {
      type: Boolean,
      default: false
    },
    isMobile: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      chatState: null,
      stateUnsubscribe: null
    }
  },
  computed: {
    // ✅ NEW: Check if chat can be ended
    canEndChat() {
      if (!this.chatState) return false;
      
      const hasConversation = this.chatState.conversation && this.chatState.conversation.$id;
      const hasCustomer = this.chatState.customer && this.chatState.customer.$id;
      const isActivePhase = ['ai', 'human'].includes(this.chatState.currentPhase);
      
      // Chat can be ended if:
      // 1. We have both conversation and customer (chat was created)
      // 2. We're in an active chat phase (AI or human)
      const canEnd = hasConversation && hasCustomer && isActivePhase;
      
      console.log('🔍 NewChatHeader canEndChat check:', {
        hasConversation,
        hasCustomer,
        currentPhase: this.chatState.currentPhase,
        isActivePhase,
        canEnd
      });
      
      return canEnd;
    }
  },
  emits: ['change-screen', 'expand-chat', 'close-widget', 'end-chat'],
  mounted() {
    // ✅ Debug: Check what companyId is being passed
    console.log('🏢 NewChatHeader - Available company IDs:', {
      'styles.companyID': this.styles.companyID,
      'styles.companyId': this.styles.companyId,
      'final used': this.styles.companyID || this.styles.companyId
    });
    
    // ✅ NEW: Subscribe to chat state to track conversation status
    this.stateUnsubscribe = chatStateManager.subscribe((newState) => {
      this.chatState = newState;
      console.log('🔄 NewChatHeader: Chat state updated:', {
        hasConversation: !!newState.conversation,
        hasCustomer: !!newState.customer,
        currentPhase: newState.currentPhase,
        canEndChat: this.canEndChat
      });
    });
    
    // Get initial state
    this.chatState = chatStateManager.getState();
  },
  
  beforeUnmount() {
    // ✅ Clean up state subscription
    if (this.stateUnsubscribe) {
      this.stateUnsubscribe();
    }
  },
  
  methods: {
  
    
    expandChat() {
      console.log('📏 NewChatHeader: Expand button clicked, current state:', this.isExpanded);
      // ✅ Toggle the expanded state and emit the new value
      const newExpandedState = !this.isExpanded;
      console.log('📏 NewChatHeader: Setting expanded to:', newExpandedState);
      this.$emit('expand-chat', newExpandedState);
    },
    
    closeWidget() {
      this.$emit('close-widget')
    },
    
    // ✅ ENHANCED: Handle end chat button click with state validation
    endChat() {
      if (!this.canEndChat) {
        console.log('⚠️ End chat clicked but chat cannot be ended:', {
          hasConversation: !!this.chatState?.conversation,
          hasCustomer: !!this.chatState?.customer,
          currentPhase: this.chatState?.currentPhase
        });
        return;
      }
      
      console.log('🔚 User clicked end chat button in header - chat can be ended');
      this.$emit('end-chat');
    }
  }
}
</script>

<style scoped>
/* Ensure disabled state is properly styled */
button:disabled {
  cursor: not-allowed !important;
}

/* Smooth transitions for state changes */
button svg {
  transition: color 0.2s ease;
}
</style>